// Prev/next buttons for .gs-rail (native scroll-snap). Loaded with defer by gs- sections.
if (!customElements.get('gs-rail-nav')) {
  customElements.define('gs-rail-nav', class extends HTMLElement {
    connectedCallback() {
      const rail = document.getElementById(this.getAttribute('for'));
      if (!rail) return;
      this.querySelectorAll('button').forEach((button) => {
        button.addEventListener('click', () => {
          const dir = button.dataset.dir === 'prev' ? -1 : 1;
          rail.scrollBy({left: dir * rail.clientWidth * 0.9, behavior: 'smooth'});
        });
      });
    }
  });
}

// Elementor-style fadeInUp for [data-gs-reveal]: only elements still below the fold wait for it,
// so nothing already on screen blinks out.
(() => {
  if (!('IntersectionObserver' in window) || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.replace('gs-reveal--pending', 'gs-reveal--in');
      io.unobserve(entry.target);
    });
  }, {rootMargin: '0px 0px -10% 0px'});
  const watch = (root) => root.querySelectorAll('[data-gs-reveal]:not(.gs-reveal--in):not(.gs-reveal--pending)').forEach((el) => {
    if (el.getBoundingClientRect().top < innerHeight) return;
    el.classList.add('gs-reveal--pending');
    io.observe(el);
  });
  watch(document);
  document.addEventListener('shopify:section:load', (e) => watch(e.target));
})();

// Scroll-scrubbed effects from gabrielasanchez.com (GSAP ScrollTrigger there, plain scroll math here).
// [data-gs-grow]: the box goes from data-w0 % / data-h0 px to full width / data-h1 px while its top
// moves from mid-screen up by data-h0 ("top center" → "bottom center"). On phones only the height
// grows, from the video's own height to data-h1m.
// [data-gs-highlight]: each letter goes from 20 % to full opacity, one after another, while the
// heading's top moves from 90 % to 20 % of the screen (SplitText chars, stagger .1, scrub).
(() => {
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  let grows = [];
  let lights = [];
  const clamp = (n) => Math.min(1, Math.max(0, n));
  const split = (el) => {
    const words = el.textContent.trim().split(/\s+/);
    el.setAttribute('aria-label', words.join(' '));
    el.textContent = '';
    const chars = [];
    words.forEach((word, i) => {
      const w = document.createElement('span');
      w.className = 'gs-hl-word';
      w.setAttribute('aria-hidden', 'true');
      for (const ch of word) {
        const c = document.createElement('span');
        c.textContent = ch;
        w.append(c);
        chars.push(c);
      }
      el.append(w);
      if (i < words.length - 1) el.append(' ');
    });
    return {el, chars};
  };
  const update = () => {
    const vh = innerHeight;
    const phone = innerWidth < 768;
    grows.forEach((el) => {
      const d = el.dataset;
      const h0 = phone ? el.parentElement.clientWidth * 9 / 16 : Number(d.h0);
      const h1 = Number(phone ? d.h1m : d.h1);
      const w0 = phone ? 100 : Number(d.w0);
      const p = clamp((vh / 2 - el.getBoundingClientRect().top) / h0);
      el.style.setProperty('--gs-grow-w', `${w0 + (100 - w0) * p}%`);
      el.style.setProperty('--gs-grow-h', `${h0 + (h1 - h0) * p}px`);
    });
    lights.forEach(({el, chars}) => {
      const p = clamp((vh * 0.9 - el.getBoundingClientRect().top) / (vh * 0.7));
      const t = p * (0.5 + 0.1 * (chars.length - 1));
      chars.forEach((c, i) => { c.style.opacity = 0.2 + 0.8 * clamp((t - 0.1 * i) / 0.5); });
    });
  };
  let queued = false;
  const queue = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; update(); });
  };
  const scan = () => {
    grows = [...document.querySelectorAll('[data-gs-grow]')];
    document.querySelectorAll('[data-gs-highlight]:not([aria-label])').forEach((el) => lights.push(split(el)));
    lights = lights.filter(({el}) => el.isConnected);
    update();
  };
  scan();
  addEventListener('scroll', queue, {passive: true});
  addEventListener('resize', queue);
  document.addEventListener('shopify:section:load', scan);
})();

// <gs-carousel>: Elementor-style carousel on a native scroll-snap track (.gs-carousel__track).
// Arrows move one slide and wrap around, dots jump to a slide, data-autoplay (ms) advances on a
// timer that pauses on hover and stops after the visitor uses the carousel, data-start opens on that
// slide (0-based).
if (!customElements.get('gs-carousel')) {
  customElements.define('gs-carousel', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.gs-carousel__track');
      if (!this.track) return;
      this.slides = [...this.track.children];
      const start = Number(this.dataset.start);
      if (start > 0) requestAnimationFrame(() => { this.track.scrollLeft = start * this.step(); });
      this.querySelectorAll('[data-dir]').forEach((button) => button.addEventListener('click', () => {
        this.stop();
        this.move(button.dataset.dir === 'prev' ? -1 : 1);
      }));
      this.dots = this.querySelector('.gs-carousel__dots');
      if (this.dots) {
        this.dots.innerHTML = this.slides.map((_, i) => `<button type="button" aria-label="${i + 1}"></button>`).join('');
        [...this.dots.children].forEach((dot, i) => dot.addEventListener('click', () => { this.stop(); this.go(i); }));
        this.track.addEventListener('scroll', () => this.mark(), {passive: true});
        this.mark();
      }
      this.track.addEventListener('pointerdown', () => this.stop(), {passive: true});
      const delay = Number(this.dataset.autoplay);
      if (delay > 0 && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.timer = setInterval(() => { if (!this.hovered) this.move(1); }, delay);
        this.addEventListener('mouseenter', () => { this.hovered = true; });
        this.addEventListener('mouseleave', () => { this.hovered = false; });
      }
    }
    disconnectedCallback() { this.stop(); }
    stop() { clearInterval(this.timer); }
    step() { return this.slides.length > 1 ? this.slides[1].offsetLeft - this.slides[0].offsetLeft : this.track.clientWidth; }
    index() { return Math.round(this.track.scrollLeft / this.step()); }
    move(dir) {
      const t = this.track;
      const atEnd = t.scrollLeft + t.clientWidth >= t.scrollWidth - 2;
      if (dir > 0 && atEnd) return this.go(0);
      if (dir < 0 && t.scrollLeft <= 2) return t.scrollTo({left: t.scrollWidth, behavior: 'smooth'});
      this.go(this.index() + dir);
    }
    go(i) { this.track.scrollTo({left: i * this.step(), behavior: 'smooth'}); }
    mark() {
      const t = this.track;
      const last = t.scrollLeft + t.clientWidth >= t.scrollWidth - 2;
      const current = last ? this.slides.length - 1 : this.index();
      [...this.dots.children].forEach((dot, i) => dot.toggleAttribute('aria-current', i === current));
    }
  });
}
